import moment from "moment";
import React from "react";
import Slider from "rc-slider";
import { MILLISECONDS_PER_DAY } from "../config/constants";
import { toDateInputValue } from "../utils/common";

type Props = {
  min: number | string;
  max: number | string;
  values?: [number | string, number | string];
  step?: number;
  label?: string;
  valueIsDate?: boolean;
  useDataMax?: boolean;
  onMinChanged?: (value: number) => void;
  onMaxChanged?: (value: number) => void;
  onAfterChange?: () => void;
  onParentDateRangeChanged?: (min: number, max: number) => void;
  registerRangeReference?: (ref: RangeInputRef) => void;
};

type RangeState = {
  currentMin: number;
  currentMax: number;
  min: number;
  max: number;
};

function toNumeric(value: number | string, isDate: boolean): number {
  if (typeof value === "number") {
    return isDate ? moment.utc(value).startOf("day").valueOf() : value;
  }

  const parsed = isDate ? moment.utc(value).startOf("day").valueOf() : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export default class RangeInput extends React.Component<Props, RangeState> {
  static defaultProps: Partial<Props> = {
    valueIsDate: false
  };

  state: RangeState = {
    currentMin: this.getInitialCurrentMin(),
    currentMax: this.getInitialCurrentMax(),
    min: toNumeric(this.props.min, this.props.valueIsDate ?? false),
    max: toNumeric(this.props.max, this.props.valueIsDate ?? false)
  };

  componentDidMount(): void {
    this.props.registerRangeReference?.(this);
    this.props.onMinChanged?.(this.state.currentMin);
    this.props.onMaxChanged?.(this.state.currentMax);
  }

  getInitialCurrentMin(): number {
    const value = this.props.values?.[0] ?? this.props.min;
    return toNumeric(value, this.props.valueIsDate ?? false);
  }

  getInitialCurrentMax(): number {
    const value = this.props.useDataMax ? this.props.max : this.props.values?.[1] ?? this.props.max;
    return toNumeric(value, this.props.valueIsDate ?? false);
  }

  fireAfterChange = (): void => {
    this.props.onAfterChange?.();
    this.props.onParentDateRangeChanged?.(this.state.currentMin, this.state.currentMax);
  };

  dateChanged(value: string, isMin: boolean): void {
    if (!value) {
      return;
    }

    const preparedValue = this.props.valueIsDate
      ? moment(value, "YYYY-MM-DD").startOf("day").valueOf()
      : Number(value);

    if (!Number.isFinite(preparedValue)) {
      return;
    }

    this.setState((prev) => ({
      ...prev,
      [isMin ? "currentMin" : "currentMax"]: preparedValue
    } as RangeState));

    if (isMin) {
      this.props.onMinChanged?.(preparedValue);
    } else {
      this.props.onMaxChanged?.(preparedValue);
    }

    this.fireAfterChange();
  }

  render() {
    const { label, valueIsDate } = this.props;

    return (
      <div className="RangeInput">
        {label ? <label>{label}</label> : null}
        <div className="range-controls">
          <input
            className="form-control"
            type={valueIsDate ? "date" : "number"}
            min={valueIsDate ? toDateInputValue(this.state.min) : this.state.min}
            max={valueIsDate ? toDateInputValue(this.state.currentMax) : this.state.currentMax}
            value={valueIsDate ? toDateInputValue(this.state.currentMin) : this.state.currentMin}
            onChange={(event) => this.dateChanged(event.target.value, true)}
          />

          <Slider
            range
            min={this.state.min}
            max={this.state.max}
            step={this.props.step ?? MILLISECONDS_PER_DAY}
            value={[this.state.currentMin, this.state.currentMax]}
            onChange={(nextValue) => {
              if (!Array.isArray(nextValue)) {
                return;
              }

              const [newMin, newMax] = nextValue;
              this.setState({ currentMin: newMin, currentMax: newMax });
              this.props.onMinChanged?.(newMin);
              this.props.onMaxChanged?.(newMax);
            }}
            onChangeComplete={this.fireAfterChange}
          />

          <input
            className="form-control"
            type={valueIsDate ? "date" : "number"}
            min={valueIsDate ? toDateInputValue(this.state.currentMin) : this.state.currentMin}
            max={valueIsDate ? toDateInputValue(this.state.max) : this.state.max}
            value={valueIsDate ? toDateInputValue(this.state.currentMax) : this.state.currentMax}
            onChange={(event) => this.dateChanged(event.target.value, false)}
          />
        </div>
      </div>
    );
  }
}

export type RangeInputRef = RangeInput;
